import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { AlertTriangle, ChevronDown, ChevronUp, Clock, CreditCard, FolderOpen, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABEL = { chest_xray: 'Chest', extremity: 'Extremity', bone_fracture: 'Extremity', abdominal: 'Abdominal' };
const STATUS_STYLE = {
  analyzed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  analyzing: 'bg-amber-50 text-amber-700 border-amber-200',
  uploaded: 'bg-stone-100 text-stone-600 border-stone-200',
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const Stat = ({ label, value, sub, testid }) => (
  <div className="p-3 rounded-lg bg-stone-50" data-testid={testid}>
    <p className="text-[10px] text-stone-500 uppercase tracking-wider">{label}</p>
    <p className="font-medium mt-0.5 break-words">{value}</p>
    {sub && <p className="text-[11px] text-stone-500 mt-0.5">{sub}</p>}
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={`text-[10px] px-2 py-0.5 rounded border capitalize ${STATUS_STYLE[status] || STATUS_STYLE.uploaded}`}>{status}</span>
);

const Findings = ({ analysis }) => {
  const list = analysis?.diseases || [];
  if (!list.length) return <p className="text-xs text-stone-500">No significant findings</p>;
  return (
    <ul className="space-y-1">
      {list.map((d, i) => (
        <li key={i} className="text-xs flex justify-between gap-3">
          <span className="text-stone-800">{d.name}</span>
          <span className="font-mono text-stone-500 shrink-0">{Math.round((d.probability || 0) * 100)}% · {d.severity}</span>
        </li>
      ))}
    </ul>
  );
};

const XrayImg = ({ filename, className, testid }) => {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <div className={`${className} flex items-center justify-center text-[10px] text-stone-400 bg-stone-100 text-center`} data-testid={testid}>Image unavailable</div>;
  }
  return <img src={`${API_URL}/api/uploads/${filename}`} alt="X-ray" className={className} loading="lazy" onError={() => setFailed(true)} data-testid={testid} />;
};

const CaseRow = ({ c }) => {
  const [open, setOpen] = useState(false);
  const top = (c.analysis?.diseases || []).slice(0, 2).map(d => d.name).join(', ');
  return (
    <div className="border border-stone-200 rounded-lg min-w-0" data-testid={`detail-case-${c.id}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-stone-50 transition-colors" data-testid={`detail-case-toggle-${c.id}`}>
        <XrayImg filename={c.xray_filename} className="w-12 h-12 rounded object-cover bg-stone-900 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-stone-900 truncate">{c.patient_name} <span className="text-stone-400 font-normal">· {c.patient_age}/{c.patient_gender?.[0]} · {c.patient_id}</span></p>
          <p className="text-xs text-stone-500 truncate">{TYPE_LABEL[c.case_type] || c.case_type} X-ray · {fmt(c.created_at)}{top ? ` · ${top}` : ''}</p>
          {c.status === 'error' && <p className="text-xs text-red-700 truncate">{c.error_message || 'Error (no details recorded)'}</p>}
        </div>
        <StatusBadge status={c.status} />
        {open ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
      </button>
      {open && <CaseExpanded c={c} />}
    </div>
  );
};

const CaseExpanded = ({ c }) => (
  <div className="grid md:grid-cols-2 gap-4 p-3 border-t border-stone-200 bg-stone-50/50 min-w-0">
    <XrayImg filename={c.xray_filename} className="w-full max-h-80 min-h-[8rem] object-contain rounded bg-black" testid={`detail-case-image-${c.id}`} />
    <div className="space-y-3 text-xs min-w-0 break-words">
      {c.clinical_notes && <p><span className="text-stone-500">Clinical notes:</span> {c.clinical_notes}</p>}
      {c.clinical_query && <p><span className="text-stone-500">Clinical query:</span> {c.clinical_query}</p>}
      {c.body_part && <p><span className="text-stone-500">Body part:</span> {c.body_part}</p>}
      {c.status === 'error' && (
        <div className="p-2 rounded bg-red-50 border border-red-200 text-red-800" data-testid={`detail-case-error-${c.id}`}>
          <p className="font-semibold">Failed at {fmt(c.error_at)}</p>
          <p className="break-words mt-1">{c.error_message || 'No error details were recorded (case failed before error tracking was added).'}</p>
        </div>
      )}
      {c.analysis && (
        <>
          <Findings analysis={c.analysis} />
          {c.analysis.summary && <p className="text-stone-700 leading-relaxed">{c.analysis.summary}</p>}
        </>
      )}
      <p className="text-stone-400">Analyzed: {fmt(c.analyzed_at)}{c.billed_to === 'bonus' ? ' · used a free case' : ''}{c.billed_to === 'refunded' ? ' · charge refunded' : ''}{c.cached_from_hash ? ' · reused cached result' : ''}</p>
    </div>
  </div>
);

const ActivityList = ({ items }) => (
  <ol className="relative border-l border-stone-200 ml-2 space-y-3" data-testid="detail-activity-list">
    {items.map((a) => (
      <li key={a.id} className="ml-4">
        <span className={`absolute -left-1.5 w-3 h-3 rounded-full border-2 border-white ${a.action.includes('fail') ? 'bg-red-500' : 'bg-[#1A4D2E]'}`} />
        <p className="text-xs font-medium text-stone-900 capitalize">{a.action.replace(/_/g, ' ')}</p>
        {a.details && <p className="text-xs text-stone-500 break-words">{a.details}</p>}
        <p className="text-[10px] text-stone-400 font-mono">{fmt(a.timestamp)}</p>
      </li>
    ))}
    {!items.length && <p className="ml-4 text-xs text-stone-500">No activity yet</p>}
  </ol>
);

const PaymentsList = ({ items }) => (
  <div className="space-y-2" data-testid="detail-payments-list">
    {items.map((p) => (
      <div key={p.id || p.order_id} className="flex items-center justify-between text-xs border border-stone-200 rounded-lg p-3">
        <div>
          <p className="font-medium text-stone-900 capitalize">{p.plan?.replace(/_/g, ' ')} {p.manual_grant && <Badge className="ml-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Complimentary</Badge>}</p>
          <p className="text-stone-500">{fmt(p.created_at)}{p.coupon_code ? ` · coupon ${p.coupon_code}` : ''}{p.note ? ` · ${p.note}` : ''}</p>
        </div>
        <div className="text-right">
          <p className="font-mono font-medium">₹{((p.amount || 0) / 100).toLocaleString()}</p>
          <p className={`capitalize ${p.status === 'paid' ? 'text-emerald-700' : 'text-stone-500'}`}>{p.status}</p>
        </div>
      </div>
    ))}
    {!items.length && <p className="text-xs text-stone-500">No payments yet</p>}
  </div>
);

const Overview = ({ data, doctor, nmcVerifyUrl, children }) => {
  const u = data?.usage || {};
  const sub = data?.doctor?.subscription || doctor.subscription || {};
  return (
    <div className="space-y-4 text-sm">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Plan" value={<span className="capitalize">{sub.plan?.replace(/_/g, ' ') || '—'}</span>} sub={sub.valid_until ? `until ${new Date(sub.valid_until).toLocaleDateString()}` : `credits: ${sub.credits ?? 0}`} testid="detail-plan" />
        <Stat label="This cycle" value={u.is_metered ? `${u.used}/${u.cap}` : 'Not metered'} sub={u.overage_count > 0 ? `+${u.overage_count} overage · ₹${u.overage_amount_inr}` : null} testid="detail-cycle-usage" />
        <Stat label="Free cases" value={u.bonus_cases || 0} sub={u.bonus_expires_at ? `until ${new Date(u.bonus_expires_at).toLocaleDateString()}` : null} testid="detail-free-cases" />
        <Stat label="Total paid" value={`₹${(data?.total_paid_inr || 0).toLocaleString()}`} sub={`${data?.payments?.length || 0} payment records`} testid="detail-total-paid" />
      </div>
      <div className="flex flex-wrap gap-2" data-testid="detail-status-counts">
        {Object.entries(data?.status_counts || {}).map(([k, v]) => (
          <span key={k} className={`text-xs px-2 py-1 rounded border capitalize ${STATUS_STYLE[k] || STATUS_STYLE.uploaded}`}>{k}: {v}</span>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="MCI/NMC Number" value={<span className="font-mono">{doctor.mci_registration_number || 'Not provided'}</span>} sub={doctor.mci_registration_number && <a href={nmcVerifyUrl(doctor)} target="_blank" rel="noopener noreferrer" className="text-[#1A4D2E] underline" data-testid="nmc-verify-btn">Verify on NMC</a>} />
        <Stat label="Phone" value={doctor.phone || 'Not provided'} />
        <Stat label="Clinic" value={doctor.clinic_name || 'Not provided'} />
        <Stat label="Specialization" value={doctor.specialization || 'Not set'} />
        <Stat label="Joined" value={fmt(doctor.created_at)} />
        <Stat label="Status" value={doctor.is_blocked ? 'Blocked' : doctor.is_verified ? 'Verified' : 'Pending'} />
      </div>
      {children}
    </div>
  );
};

export const DoctorDetailDialog = ({ doctor, onClose, nmcVerifyUrl, roleBadge, children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!doctor?._id) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true); setError(''); setData(null);
      try {
        const res = await api.get(`/admin/doctors/${doctor._id}/details`);
        if (active) setData(res.data);
      } catch (e) {
        if (active) setError(e.response?.data?.detail || 'Failed to load details');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [doctor?._id]);

  const cases = data?.cases || [];
  const failed = cases.filter(c => c.status === 'error');

  return (
    <Dialog open={!!doctor} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="doctor-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">{doctor?.name} {roleBadge(doctor?.role)}</DialogTitle>
          <DialogDescription>{doctor?.email}</DialogDescription>
        </DialogHeader>
        {doctor && (
          <Tabs defaultValue="overview" className="w-full min-w-0">
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="overview" data-testid="detail-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="cases" className="gap-1" data-testid="detail-tab-cases"><FolderOpen className="w-3.5 h-3.5" /> Cases ({cases.length})</TabsTrigger>
              <TabsTrigger value="failed" className="gap-1" data-testid="detail-tab-failed"><AlertTriangle className="w-3.5 h-3.5" /> Failed ({failed.length})</TabsTrigger>
              <TabsTrigger value="activity" className="gap-1" data-testid="detail-tab-activity"><Activity className="w-3.5 h-3.5" /> Activity</TabsTrigger>
              <TabsTrigger value="payments" className="gap-1" data-testid="detail-tab-payments"><CreditCard className="w-3.5 h-3.5" /> Payments</TabsTrigger>
            </TabsList>
            {loading && <p className="text-xs text-stone-500 py-4 flex items-center gap-1" data-testid="detail-loading"><Clock className="w-3 h-3" /> Loading details…</p>}
            {error && <p className="text-xs text-red-700 py-4" data-testid="detail-error">{error}</p>}
            <TabsContent value="overview" className="mt-4">
              <Overview data={data} doctor={doctor} nmcVerifyUrl={nmcVerifyUrl}>{children}</Overview>
            </TabsContent>
            <TabsContent value="cases" className="mt-4 space-y-2" data-testid="detail-cases-list">
              {cases.map(c => <CaseRow key={c.id} c={c} />)}
              {data && !cases.length && <p className="text-xs text-stone-500">No cases uploaded yet</p>}
            </TabsContent>
            <TabsContent value="failed" className="mt-4 space-y-2" data-testid="detail-failed-list">
              {failed.map(c => <CaseRow key={c.id} c={c} />)}
              {data && !failed.length && <p className="text-xs text-stone-500">No failed analyses</p>}
            </TabsContent>
            <TabsContent value="activity" className="mt-4"><ActivityList items={data?.activity || []} /></TabsContent>
            <TabsContent value="payments" className="mt-4"><PaymentsList items={data?.payments || []} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
